import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ErrorBoundary from './components/ErrorBoundary';
// aws-config is loaded in index.tsx
import './App.css';

// Import AuzLand components
import Header from './auzland/Header';
import Footer from './auzland/Footer';
import HomePage from './auzland/HomePage';
import PropertiesPage from './auzland/PropertiesPage';
import BlogPage from './auzland/pages/BlogPage';
import AboutPage from './auzland/AboutPage';
import ContactPage from './auzland/ContactPage';

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="employee-portal">
        <Login />
      </div>
    );
  }

  // Both user types now use the same Dashboard component
  // The Dashboard automatically shows/hides features based on user groups
  return (
    <div className="employee-portal">
      <ErrorBoundary fallback={
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Dashboard Error</h2>
          <p>There was an error loading the dashboard. Please try refreshing the page.</p>
          <button onClick={() => window.location.reload()}>Refresh Page</button>
        </div>
      }>
        <Dashboard />
      </ErrorBoundary>
    </div>
  );
};


// Public customer-facing routes component
const PublicRoutes: React.FC = () => (
  <>
    <Routes>
      <Route path="/" element={
        <div className="App">
          <Header />
          <main>
            <HomePage />
          </main>
          <Footer />
        </div>
      } />
      
      <Route path="/buy" element={
        <div className="App">
          <Header />
          <main>
            <PropertiesPage />
          </main>
          <Footer />
        </div>
      } />
      
      <Route path="/sell" element={
        <div className="App">
          <Header />
          <main>
            <PropertiesPage />
          </main>
          <Footer />
        </div>
      } />
      
      <Route path="/blogs" element={
        <div className="App">
          <Header />
          <main>
            <BlogPage />
          </main>
          <Footer />
        </div>
      } />
      
      <Route path="/about" element={
        <div className="App">
          <Header />
          <main>
            <AboutPage />
          </main>
          <Footer />
        </div>
      } />
      
      <Route path="/contact" element={
        <div className="App">
          <Header />
          <main>
            <ContactPage />
          </main>
          <Footer />
        </div>
      } />
      
      {/* Redirect unknown routes to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    <a
      href="https://calendar.app.google/ML4z6uLYNeycwxwq7"
      target="_blank"
      rel="noopener noreferrer"
      className="sticky-appointment-btn"
    >
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0h18M9.75 12.75h.008v.008h-.008v-.008zm0 4.5h.008v.008h-.008v-.008zm4.5-4.5h.008v.008h-.008v-.008zm0 4.5h.008v.008h-.008v-.008z" />
      </svg>
      <span className="button-text">Book Appointment</span>
    </a>
  </>
);

// Employee portal routes component (wrapped with AuthProvider)
const EmployeePortal: React.FC = () => (
  <AuthProvider>
    <Routes>
      {/* Owner/Employee Portal - ONLY at /properties */}
      <Route index element={<AppContent />} />
      
      {/* Redirect any other /properties/* routes to /properties */}
      <Route path="*" element={<Navigate to="/properties" replace />} />
    </Routes>
  </AuthProvider>
);

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <Router>
        <Routes>
          {/* Employee portal routes - WITH authentication (must come first) */}
          <Route path="/properties/*" element={<EmployeePortal />} />
          
          {/* Public customer-facing routes - NO authentication */}
          <Route path="/*" element={<PublicRoutes />} />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
};

export default App;
